import {
  InvoiceTransformationConditionOperatorEnum,
  InvoiceTransformationRequestUi,
  ProductTypeEnum,
} from '@monetize/types/app';
import { FC, ReactNode, useMemo } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { composeGetQuery } from '../../api/utils';
import {
  OFFERING_STATUS_DISPLAY,
  OFFERING_TYPES_ENUM_DISPLAY,
  RATE_BILLING_FREQUENCY_DISPLAY,
  SUBSCRIPTION_TIMING_OPTIONS,
} from '../../constants/offerings';
import {
  PRODUCT_TYPE_DISPLAY,
  PRODUCT_TYPE_DISPLAY_SHORT,
} from '../../constants/products';
import { QuoteStatusEnumDisplay } from '../../constants/quotes';
import { MFlex, MInput, MText } from '../Monetize';
import { MCustomMultiSelect } from '../Monetize/MCustomSelect/MCustomMultiSelect';
import { MCustomSelect } from '../Monetize/MCustomSelect/MCustomSelect';
import { Highlighter } from '../Monetize/MPageSearchInput/Highlighter';
import { MultiValueInput } from './MultiValueInput';
import { FieldKind, isMultiValueOperator } from './invoiceTransformation.utils';

interface InvoiceTransformationConditionValueProps {
  prefix: `invoiceLineRules.${number}.conditionGroups.${number}.conditions.${number}`;
  formContext: UseFormReturn<InvoiceTransformationRequestUi>;
  fieldKind: FieldKind;
  operator: InvoiceTransformationConditionOperatorEnum;
  isDisabled: boolean;
}

const referenceListConfig = composeGetQuery({
  first: 0,
  rows: 50,
  page: 0,
  sortField: 'name',
  sortOrder: 1,
});

function enumToOptions<T extends string>(
  display: Record<T, string>,
): Array<{ value: T; title: string }> {
  return (Object.keys(display) as T[]).map((value) => ({
    value,
    title: display[value],
  }));
}

const OFFERING_TYPE_OPTIONS = enumToOptions(OFFERING_TYPES_ENUM_DISPLAY);
const PRODUCT_TYPE_OPTIONS = enumToOptions(
  PRODUCT_TYPE_DISPLAY as Record<ProductTypeEnum, string>,
);

type ItemRenderArgs = {
  item: any;
  query?: string;
};

function renderRichItem({
  row2Item1Fn,
  row2Item2Fn,
  row3Item1Fn,
  row3Item2Fn,
}: {
  row2Item1Fn: (item: any) => ReactNode;
  row2Item2Fn?: (item: any) => ReactNode;
  row3Item1Fn?: (item: any) => ReactNode;
  row3Item2Fn?: (item: any) => ReactNode;
}) {
  return ({ item, query }: ItemRenderArgs) => (
    <MFlex
      direction="column"
      overflow="hidden"
      whiteSpace="nowrap"
      textOverflow="ellipsis"
      w="100%"
    >
      <MFlex justifyContent="space-between" w="100%">
        <Highlighter
          title={item.name}
          color="tPurple.dark"
          fontSize="sm"
          fontWeight="medium"
          textToHighlight={item.name}
          searchWords={[query ?? '']}
          whiteSpace="normal"
        />
      </MFlex>
      <MFlex justifyContent="space-between" w="100%">
        <MText color="tGray.darkPurple" fontSize="xs">
          {row2Item1Fn(item)}
        </MText>
        {row2Item2Fn && (
          <MText color="tGray.darkPurple" fontSize="xs">
            {row2Item2Fn(item)}
          </MText>
        )}
      </MFlex>
      {row3Item1Fn && (
        <MFlex justifyContent="space-between">
          <MText color="tGray.darkPurple" fontSize="xs">
            {row3Item1Fn(item)}
          </MText>
          {row3Item2Fn && (
            <MText color="tGray.darkPurple" fontSize="xs">
              {row3Item2Fn(item)}
            </MText>
          )}
        </MFlex>
      )}
    </MFlex>
  );
}

const STATUS_DISPLAY: Record<string, string> = {
  ...OFFERING_STATUS_DISPLAY,
  ...QuoteStatusEnumDisplay,
};

const offeringRenderItem = renderRichItem({
  row2Item1Fn: (item) =>
    OFFERING_TYPES_ENUM_DISPLAY[
      item.type as keyof typeof OFFERING_TYPES_ENUM_DISPLAY
    ],
  row3Item1Fn: (item) => item.id,
  row3Item2Fn: (item) => STATUS_DISPLAY[item.status] ?? item.status,
});

const productRenderItem = renderRichItem({
  row2Item1Fn: (item) =>
    PRODUCT_TYPE_DISPLAY_SHORT[
      item.productType as keyof typeof PRODUCT_TYPE_DISPLAY_SHORT
    ],
  row3Item1Fn: (item) => item.id,
  row3Item2Fn: (item) => STATUS_DISPLAY[item.status] ?? item.status,
});

const rateRenderItem = renderRichItem({
  row2Item1Fn: (item) =>
    RATE_BILLING_FREQUENCY_DISPLAY[
      item.billingFrequency as keyof typeof RATE_BILLING_FREQUENCY_DISPLAY
    ],
  row2Item2Fn: (item) =>
    SUBSCRIPTION_TIMING_OPTIONS[
      item.subscriptionTiming as keyof typeof SUBSCRIPTION_TIMING_OPTIONS
    ],
  row3Item1Fn: (item) => item.id,
  row3Item2Fn: (item) => STATUS_DISPLAY[item.status] ?? item.status,
});

export const InvoiceTransformationConditionValue: FC<
  InvoiceTransformationConditionValueProps
> = ({ prefix, formContext, fieldKind, operator, isDisabled }) => {
  const { control } = formContext;
  const isMulti = isMultiValueOperator(operator);
  // CONTAINS only supports a free-text substring match — even for typed fields
  const isContains =
    operator === InvoiceTransformationConditionOperatorEnum.CONTAINS;

  const referenceConfig = useMemo(() => {
    switch (fieldKind) {
      case 'OFFERING_REF':
        return {
          endpoint: '/api/offerings/summary',
          getByIdEndpointFn: (recordId: string) =>
            `/api/offerings/summary?id=${recordId}`,
          loadAll: true,
          renderItemContent: offeringRenderItem,
        };
      case 'PRODUCT_REF':
        return {
          endpoint: '/api/products',
          getByIdEndpointFn: (recordId: string) => `/api/products/${recordId}`,
          loadAll: true,
          renderItemContent: productRenderItem,
        };
      case 'RATE_REF':
        return {
          endpoint: '/api/rates',
          getByIdEndpointFn: (recordId: string) => `/api/rates/${recordId}`,
          loadAll: false,
          renderItemContent: rateRenderItem,
        };
      default:
        return null;
    }
  }, [fieldKind]);

  const enumOptions = useMemo(() => {
    if (fieldKind === 'OFFERING_TYPE') {
      return OFFERING_TYPE_OPTIONS;
    }
    if (fieldKind === 'PRODUCT_TYPE') {
      return PRODUCT_TYPE_OPTIONS;
    }
    return null;
  }, [fieldKind]);

  // Free-text fallback: STRING fields, or any field with CONTAINS operator
  if (fieldKind === 'STRING' || isContains) {
    if (isMulti) {
      return (
        <Controller
          name={`${prefix}.values`}
          control={control}
          render={({ field: { value, onChange, onBlur } }) => (
            <MultiValueInput
              value={value || []}
              onChange={onChange}
              onBlur={onBlur}
              isDisabled={isDisabled}
            />
          )}
        />
      );
    }
    return (
      <Controller
        name={`${prefix}.value`}
        control={control}
        render={({ field: { value, ...rest } }) => (
          <MInput
            placeholder="Value"
            value={value ?? ''}
            isDisabled={isDisabled}
            {...rest}
          />
        )}
      />
    );
  }

  if (referenceConfig) {
    if (isMulti) {
      return (
        <Controller
          name={`${prefix}.values`}
          control={control}
          render={({ field: { value, onChange, ...rest } }) => (
            <MCustomMultiSelect
              showQueryInput
              showCheckCount
              closeButtonText="Apply"
              isDisabled={isDisabled}
              endpoint={referenceConfig.endpoint}
              getByIdEndpointFn={referenceConfig.getByIdEndpointFn}
              loadAll={referenceConfig.loadAll}
              renderItemContent={referenceConfig.renderItemContent}
              additionalSearchParams={referenceListConfig}
              itemTitle="name"
              itemValue="id"
              value={value || []}
              onChange={(next) => onChange(next)}
              renderContentInInput={() => (
                <MText fontSize="sm" color="tPurple.dark">
                  {(value || []).length} selected
                </MText>
              )}
              popOverProps={{
                positioning: { placement: 'bottom-end', sameWidth: false },
              }}
              popOverContentProps={{ minW: '320px' }}
              {...rest}
            />
          )}
        />
      );
    }
    return (
      <Controller
        name={`${prefix}.value`}
        control={control}
        render={({ field: { value, onChange, ...rest } }) => (
          <MCustomSelect
            useHighlightedItemList
            showQueryInput
            isDisabled={isDisabled}
            endpoint={referenceConfig.endpoint}
            getByIdEndpointFn={referenceConfig.getByIdEndpointFn}
            loadAll={referenceConfig.loadAll}
            renderItemContent={referenceConfig.renderItemContent}
            additionalSearchParams={referenceListConfig}
            itemTitle="name"
            itemValue="id"
            value={value ?? ''}
            onChange={(next) => onChange(next)}
            popOverProps={{
              positioning: { placement: 'bottom-end', sameWidth: false },
            }}
            popOverContentProps={{ minW: '320px' }}
            {...rest}
          />
        )}
      />
    );
  }

  if (enumOptions) {
    if (isMulti) {
      return (
        <Controller
          name={`${prefix}.values`}
          control={control}
          render={({ field: { value, onChange, ...rest } }) => {
            const selected = value || [];
            const titles = selected
              .map(
                (entry) =>
                  enumOptions.find((opt) => opt.value === entry)?.title ??
                  entry,
              )
              .filter(Boolean);
            return (
              <MCustomMultiSelect
                showQueryInput
                showCheckCount
                closeButtonText="Apply"
                isDisabled={isDisabled}
                items={enumOptions}
                itemTitle="title"
                itemValue="value"
                value={selected}
                onChange={(next) => onChange(next)}
                renderContentInInput={() =>
                  titles.length > 0 ? (
                    <MText fontSize="sm" truncate>
                      {titles.join(', ')}
                    </MText>
                  ) : null
                }
                popOverProps={{
                  positioning: { placement: 'bottom-end', sameWidth: false },
                }}
                popOverContentProps={{ minW: '320px' }}
                {...rest}
              />
            );
          }}
        />
      );
    }
    return (
      <Controller
        name={`${prefix}.value`}
        control={control}
        render={({ field: { value, onChange, ...rest } }) => (
          <MCustomSelect
            items={enumOptions}
            itemTitle="title"
            itemValue="value"
            value={value ?? ''}
            onChange={(next) => onChange(next)}
            isDisabled={isDisabled}
            {...rest}
          />
        )}
      />
    );
  }

  return null;
};
